'use client';

import '@/features/gate/tokens';

import { Dialog, DialogContent } from '@/components/ui/dialog';
import { GateBanner } from '@/features/gate/variants/GateBanner/GateBanner';
import type { GateBannerData } from '@/features/gate/variants/GateBanner/types';
import { GatePopover, type GatePopoverAlign } from '@/features/gate/primitives/GatePopover';
import { PaywallDialog } from '@/features/gate/variants/PaywallDialog/PaywallDialog';
import type {
  PaywallDialogData,
  PaywallValueBullet,
} from '@/features/gate/variants/PaywallDialog/types';
import { AnalyticsEvent } from '@/shared/lib/analytics/events';
import posthog from 'posthog-js';
import { useEffect, type ReactElement } from 'react';
import type { SubscriptionModalContext } from '../hooks/use-guest-gate';

interface SubscriptionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  context: SubscriptionModalContext | null;
}

const BANNER_COPY: Record<
  'like' | 'save' | 'comment' | 'clip',
  { eyebrow: string; title: string; body: string }
> = {
  like: {
    eyebrow: 'Sign in to like',
    title: 'Want to like this?',
    body: 'Sign in to engage with creators and shape recommendations.',
  },
  save: {
    eyebrow: 'Sign in to save',
    title: 'Want to save this?',
    body: 'Sign in to build your personal watchlist.',
  },
  comment: {
    eyebrow: 'Sign in to comment',
    title: 'Want to comment?',
    body: 'Sign in to join the conversation.',
  },
  clip: {
    eyebrow: 'Subscribe to clip',
    title: 'Want to share a clip?',
    body: 'Subscribers can clip their favorite moments and share them anywhere.',
  },
};

function trackCta(destination: string, source: string) {
  posthog.capture(AnalyticsEvent.FREEMIUM_CONVERSION_CTA_CLICKED, {
    source,
    destination,
  });
}

function buildPaywallBullets(creatorName: string): ReadonlyArray<PaywallValueBullet> {
  return [
    {
      title: `**Every locked video from ${creatorName}**`,
      detail: 'Exclusives, field notes, the full catalog.',
    },
    {
      title: '**Every creator. Every exclusive.**',
      detail: 'All creators on Taboo TV. Every region. No extra fee.',
    },
    { title: '**No ads, no mid-video paywall**' },
  ];
}

function buildPaywallData(
  context: Extract<SubscriptionModalContext, { type: 'locked-tab' } | { type: 'locked-video' }>
): PaywallDialogData {
  const peekVideos =
    context.type === 'locked-tab' && context.peekVideos !== undefined ? context.peekVideos : [];
  const totalLockedCount =
    context.type === 'locked-tab' && context.totalLockedCount !== undefined
      ? context.totalLockedCount
      : 0;
  const overflowCount = totalLockedCount - peekVideos.length;
  const creatorName = context.type === 'locked-tab' ? context.creatorName : '';
  const creatorAvatarSrc = context.type === 'locked-tab' ? context.creatorAvatarSrc : undefined;
  const eyebrow =
    context.type === 'locked-tab' ? `Locked · ${creatorName}` : `Exclusive · ${context.videoTitle}`;
  const headline =
    context.type === 'locked-tab'
      ? `Unlock **${context.tabName.toLowerCase()}** from ${creatorName}.`
      : "You're about to go **uncut**.";
  const sub =
    context.type === 'locked-tab'
      ? `${creatorName}'s ${context.tabName.toLowerCase()} are part of the subscription. One membership, every exclusive.`
      : `This video is part of the subscription. One membership, every exclusive on Taboo TV.`;

  return {
    eyebrow,
    headline,
    sub,
    peekVideos,
    ...(overflowCount > 0 ? { overflowNote: `// + ${overflowCount} more locked episodes` } : {}),
    valueBullets: buildPaywallBullets(creatorName || 'this creator'),
    ...(creatorName !== '' ? { creatorTag: creatorName.toUpperCase() } : {}),
    ...(creatorAvatarSrc !== undefined ? { creatorAvatarSrc } : {}),
    ctaLabel: 'Choose a plan',
    ctaHref: '/choose-plan',
    trustText: 'CANCEL ANYTIME · ALL DEVICES\n1 PLAN · EVERY CREATOR',
    notNowLabel: 'Not now — keep browsing free',
  };
}

function pickAlign(anchorEl: HTMLElement | null): GatePopoverAlign {
  if (anchorEl === null || typeof window === 'undefined') return 'center';
  const rect = anchorEl.getBoundingClientRect();
  const centerX = rect.left + rect.width / 2;
  return centerX < window.innerWidth / 2 ? 'start' : 'end';
}

function buildBannerData(
  context: Extract<SubscriptionModalContext, { type: 'like' | 'save' | 'comment' | 'clip' }>
): GateBannerData {
  const copy = BANNER_COPY[context.type];
  return {
    eyebrow: copy.eyebrow,
    title: copy.title,
    body: copy.body,
    ctaLabel: 'Choose a plan',
    ctaHref: '/choose-plan',
    dismissLabel: 'Keep browsing free',
  };
}

/**
 * Dispatches the right gate UI based on context:
 *   - `like` / `save` / `comment` → anchored `GatePopover` + `GateBanner`
 *     animating out of the trigger button.
 *   - `locked-tab` / `locked-video` → rich `PaywallDialog` inside a
 *     platform `Dialog` for focus trap + backdrop.
 */
export function SubscriptionModal({
  open,
  onOpenChange,
  context,
}: SubscriptionModalProps): ReactElement | null {
  // Track CTA click via anchor taps — the CTA link itself is inside the primitive
  // and uses a plain <a>. We capture analytics on close when a CTA href was clicked;
  // a simpler approach: record the trigger on open and let PostHog's autocapture
  // pick up the subsequent /choose-plan navigation. We intentionally do NOT wrap
  // the CTA in an onClick here because the primitive is closed-world.
  useEffect(() => {
    if (!open || context === null) return;
    trackCta('/choose-plan', `gate_${context.type}`);
  }, [open, context]);

  if (context === null) return null;

  if (
    context.type === 'like' ||
    context.type === 'save' ||
    context.type === 'comment' ||
    context.type === 'clip'
  ) {
    const anchorEl = context.anchorEl ?? null;
    return (
      <GatePopover
        anchorEl={anchorEl}
        open={open}
        onClose={() => onOpenChange(false)}
        side="bottom"
        align={pickAlign(anchorEl)}
        ariaLabel={BANNER_COPY[context.type].title}
      >
        <GateBanner
          data={buildBannerData(context)}
          onDismiss={() => {
            posthog.capture(AnalyticsEvent.FREEMIUM_BANNER_DISMISSED, {
              context: context.type,
            });
            onOpenChange(false);
          }}
        />
      </GatePopover>
    );
  }

  // locked-tab / locked-video → rich paywall
  const data = buildPaywallData(context);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showClose={false}
        className="max-w-[780px] bg-transparent border-0 shadow-none rounded-none p-0"
      >
        <PaywallDialog data={data} onClose={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}
